import { useNavigate } from "react-router-dom";
import { useSavedBlips } from "@/hooks/useBlipQueries";
import { BlipsCard } from "@/components/shared/BlipsCard";
import { SectionHeader } from "@/components/shared/SectionHeader";
import { useAuth } from "@/contexts";
import { Navigate } from "react-router-dom";
import { useMemo, useCallback } from "react";

export default function SavedBlipsPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const { data, isLoading, isError, error } = useSavedBlips(1, 20);

  // Handle product click - navigate to blip feed with the specific blip
  const handleBlipClick = useCallback(
    (blipId: string, productId: string) => {
      navigate(`/blips?blipId=${blipId}&productId=${productId}`);
    },
    [navigate]
  );

  // Memoize click handlers to prevent unnecessary re-renders
  const clickHandlers = useMemo(() => {
    const handlers = new Map<string, () => void>();
    if (data?.blips) {
      data.blips.forEach((blip) => {
        if (blip.blipId && blip.productId) {
          handlers.set(blip.blipId, () =>
            handleBlipClick(blip.blipId!, blip.productId!)
          );
        }
      });
    }
    return handlers;
  }, [data?.blips, handleBlipClick]);

  // Show loading state
  if (isAuthLoading || isLoading) {
    return (
      <div className="w-full min-h-screen bg-[#F4F4F4] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#16783A]" />
          <p className="text-gray-600 text-sm">Loading saved blips...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: "/saved-blips" }} replace />;
  }

  // Show error state
  if (isError) {
    return (
      <div className="w-full min-h-screen bg-[#F4F4F4] flex items-center justify-center px-4">
        <div className="flex flex-col items-center gap-4 text-center max-w-md">
          <div className="text-red-500 text-4xl">⚠️</div>
          <p className="text-gray-600 text-lg font-medium">
            Failed to load saved blips
          </p>
          <p className="text-gray-500 text-sm">
            {error?.message || "Something went wrong"}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-[#16783A] text-white rounded-lg hover:bg-[#125a2d] transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Show empty state
  if (!data?.blips || data.blips.length === 0) {
    return (
      <div className="w-full min-h-screen bg-[#F4F4F4]">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <SectionHeader title="Saved Blips" />
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="text-gray-400 text-6xl mb-4">📌</div>
            <p className="text-gray-600 text-lg font-medium mb-2">
              No saved blips yet
            </p>
            <p className="text-gray-500 text-sm text-center max-w-md">
              Start saving blips you like to view them here later
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-[#F4F4F4] px-4 py-4">
      <div className="max-w-7xl mx-auto px-4 py-4 min-h-screen rounded-xs bg-white">
        <SectionHeader title="Saved Blips" />
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {data.blips.map((blip) => (
            <BlipsCard
              key={blip.blipId || blip._id}
              product={blip}
              onClick={clickHandlers.get(blip.blipId || "")}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

